import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import type { LedgerDrawerProps } from '../types';

import {
  closeLedgerDrawer,
  openLedgerDrawer as openLedgerDrawerAction,
} from './slice';

/**
 * Read the drawer's current props (null when closed).
 *
 * Used by `PatientLedgerDrawerHost`. Application code should usually
 * trigger via `useOpenLedgerDrawer` instead.
 */
export function useLedgerDrawerProps(): LedgerDrawerProps | null {
  return useAppSelector(
    (state) =>
      (state as { 'patientLedger/drawer'?: { props: LedgerDrawerProps | null } })[
        'patientLedger/drawer'
      ]?.props ?? null,
  );
}

/**
 * Convenience trigger for opening the Patient Ledger Drawer.
 *
 * Replaces the old patchwork of `useModal().open(MODAL_TYPES.PAYMENT_REQUEST, ...)`,
 * `MODAL_TYPES.RECORD_PAYMENT`, `MODAL_TYPES.WRITE_OFF`, `MODAL_TYPES.CAPTURE_CHARGES`,
 * and the inline LogCollectionForm — all eight legacy surfaces funnel here.
 *
 * Calling this twice doesn't stack — the second call updates the props in
 * place. That gives us "destination surface" semantics with no special
 * coordination required from triggers.
 */
export function useOpenLedgerDrawer(): (props: LedgerDrawerProps) => void {
  const dispatch = useAppDispatch();
  return useCallback(
    (props: LedgerDrawerProps) => {
      if (!props.patientId) return;
      dispatch(openLedgerDrawerAction(props));
    },
    [dispatch],
  );
}

export function useCloseLedgerDrawer(): () => void {
  const dispatch = useAppDispatch();
  return useCallback(() => {
    dispatch(closeLedgerDrawer());
  }, [dispatch]);
}
